import { Helmet } from "react-helmet-async";
import { Link, useParams } from "wouter";
import { Navbar } from "@/components/layout/Navbar";
import { getUseCase, getRelated, PLAN_META, PLAN_CTA_HREF, type UseCase } from "@/data/useCases";
import { ArrowLeft, ArrowRight, CheckCircle2, MapPin, Zap } from "lucide-react";

// ── Mini phone mockup ──────────────────────────────────────────────────────

function MiniPhone({ uc }: { uc: UseCase }) {
  const meta = PLAN_META[uc.plan];
  const mockLinks = [
    { label: "Learn More", color: "bg-primary" },
    { label: "Contact Us", color: "bg-slate-700" },
    { label: "Book Now", color: "bg-emerald-600" },
    { label: "View Profile", color: "bg-violet-600" },
  ];

  return (
    <div className="relative mx-auto" style={{ width: 240 }}>
      <div className="bg-slate-900 rounded-[2.8rem] p-[9px] shadow-2xl ring-1 ring-white/10">
        <div className="bg-slate-800 rounded-t-[2.2rem] pt-2.5 pb-0">
          <div className="w-18 h-5 bg-slate-900 rounded-full mx-auto mb-2 w-16" />
        </div>
        <div className="bg-white rounded-[1.8rem] overflow-hidden" style={{ height: 420 }}>
          {/* Cover gradient */}
          <div className={`h-24 bg-gradient-to-br ${meta.hero} relative`}>
            <div className="absolute top-2.5 right-2.5">
              <span className="text-[9px] font-bold bg-amber-400 text-amber-900 px-2 py-0.5 rounded-full">DEMO</span>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white/20 to-transparent" />
          </div>
          {/* Avatar */}
          <div className="px-4 -mt-8 mb-3">
            <div className={`w-16 h-16 rounded-full border-[3px] border-white flex items-center justify-center text-3xl shadow-lg bg-white`}>
              {uc.icon}
            </div>
          </div>
          {/* Name & info */}
          <div className="px-4 pb-3">
            <p className="font-bold text-sm leading-tight">{uc.title}</p>
            <p className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-0.5">
              <MapPin className="w-2.5 h-2.5" /> Your City
            </p>
            <p className="text-[10px] text-slate-600 mt-2 leading-relaxed line-clamp-2">
              {uc.description}
            </p>
          </div>
          {/* Mock links */}
          <div className="px-4 space-y-2">
            {mockLinks.map((l) => (
              <div
                key={l.label}
                className={`${l.color} text-white rounded-xl py-2.5 text-center text-[11px] font-semibold`}
              >
                {l.label}
              </div>
            ))}
          </div>
        </div>
        <div className="bg-slate-800 rounded-b-[2.2rem] pt-2 pb-1">
          <div className="w-16 h-1 bg-slate-600 rounded-full mx-auto" />
        </div>
      </div>
    </div>
  );
}

// ── Related card ───────────────────────────────────────────────────────────

function RelatedCard({ uc }: { uc: UseCase }) {
  const meta = PLAN_META[uc.plan];
  return (
    <Link
      href={`/use-cases/${uc.slug}`}
      className="group block bg-card border border-border/60 rounded-2xl p-5 hover:shadow-lg hover:border-primary/30 hover:-translate-y-0.5 transition-all"
    >
      <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center text-xl mb-3 group-hover:scale-110 transition-transform">
        {uc.icon}
      </div>
      <span className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full mb-2 ${meta.bg} ${meta.text}`}>
        {meta.badge}
      </span>
      <h4 className="font-bold text-sm mb-1 group-hover:text-primary transition-colors">{uc.title}</h4>
      <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{uc.description}</p>
      <span className="mt-3 text-xs font-semibold text-primary flex items-center gap-1 group-hover:gap-2 transition-all">
        Learn more <ArrowRight className="w-3 h-3" />
      </span>
    </Link>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function UseCaseDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const uc = getUseCase(slug || "");

  if (!uc) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-xl mx-auto px-4 py-32 text-center">
          <p className="text-5xl mb-4">🔍</p>
          <h1 className="text-2xl font-bold mb-3">Use case not found</h1>
          <p className="text-muted-foreground mb-8">We couldn't find that use case page.</p>
          <Link href="/use-cases" className="px-6 py-3 bg-primary text-primary-foreground font-bold rounded-xl inline-block">
            ← Browse all use cases
          </Link>
        </div>
      </div>
    );
  }

  const meta = PLAN_META[uc.plan];
  const ctaHref = PLAN_CTA_HREF[uc.plan];
  const related = getRelated(uc, 3);

  return (
    <>
      <Helmet>
        <title>{uc.metaTitle}</title>
        <meta name="description" content={uc.metaDescription} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={uc.metaTitle} />
        <meta property="og:description" content={uc.metaDescription} />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Navbar />

        {/* ── Section 1: Hero ── */}
        <section className={`bg-gradient-to-br ${meta.hero} text-white relative overflow-hidden`}>
          <div
            className="absolute inset-0 opacity-10"
            style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "28px 28px" }}
          />
          <div className="relative max-w-5xl mx-auto px-6 py-24 text-center">
            {/* Breadcrumb */}
            <div className="flex items-center justify-center gap-2 text-white/60 text-sm mb-8">
              <Link href="/use-cases" className="hover:text-white transition-colors flex items-center gap-1">
                <ArrowLeft className="w-3.5 h-3.5" /> Use Cases
              </Link>
              <span>/</span>
              <span className="text-white/80">{uc.title}</span>
            </div>

            {/* Icon */}
            <div className="w-20 h-20 rounded-3xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-4xl mx-auto mb-6 shadow-xl">
              {uc.icon}
            </div>

            {/* Plan badge */}
            <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-6 ${meta.bg} ${meta.text}`}>
              <Zap className="w-3 h-3" /> {meta.badge} PLAN
            </span>

            {/* Dream headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-tight mb-6 max-w-3xl mx-auto">
              {uc.dream}
            </h1>

            {/* Subtitle */}
            <p className="text-lg text-white/70 max-w-2xl mx-auto mb-10">
              {uc.description}
            </p>

            {/* CTA */}
            <Link
              href={ctaHref}
              className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold px-8 py-4 rounded-xl shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all text-base"
            >
              {uc.cta} <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>

        {/* ── Sections 2–7 ── */}
        <div className="max-w-5xl mx-auto px-6 py-20 space-y-24">

          {/* ── Section 2: The situation today ── */}
          <section>
            <div className="max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
                <span className="w-8 h-px bg-border" /> The situation today
              </div>
              <h2 className="text-3xl font-display font-bold mb-8">
                The problem with how things work right now
              </h2>
              <div className="space-y-4">
                {uc.situation.map((point, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-4 p-5 bg-red-50 border border-red-100 rounded-2xl"
                  >
                    <div className="w-7 h-7 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-sm font-bold shrink-0 mt-0.5">
                      {i + 1}
                    </div>
                    <p className="text-sm leading-relaxed text-foreground/80">{point}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── Section 3: How CommonQR changes this ── */}
          <section>
            <div className="max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
                <span className="w-8 h-px bg-border" /> How CommonQR changes this
              </div>
              <h2 className="text-3xl font-display font-bold mb-8">
                Three ways CommonQR solves it
              </h2>
              <div className="space-y-4">
                {uc.solution.map((point, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-4 p-5 bg-emerald-50 border border-emerald-100 rounded-2xl"
                  >
                    <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0 mt-0.5" />
                    <p className="text-sm leading-relaxed text-foreground/80">{point}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── Section 4: What they see when scanned ── */}
          <section className="bg-muted/30 rounded-3xl p-8 sm:p-12">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
                  <span className="w-8 h-px bg-border" /> What they see when scanned
                </div>
                <h2 className="text-3xl font-display font-bold mb-4">
                  A living profile that always says the right thing
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  When someone scans your CommonQR, they land on a beautiful, branded profile page — with all your links, contact options, and content. Update it any time without changing the QR code.
                </p>
                <ul className="space-y-3">
                  {[
                    "Works on any smartphone — no app download required",
                    "Opens instantly — loads in under 1 second",
                    "Update the content without reprinting the QR",
                    "Analytics track every scan, location, and device",
                  ].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex justify-center">
                <MiniPhone uc={uc} />
              </div>
            </div>
          </section>

          {/* ── Section 5: Plan badge ── */}
          <section className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
              <span className="w-8 h-px bg-border" /> Which plan includes this
            </div>
            <div className={`rounded-2xl border-2 ${meta.border} ${meta.bg} p-8`}>
              <div className="flex items-start gap-5">
                <div className={`text-4xl rounded-2xl p-3 bg-card shadow-sm`}>{uc.icon}</div>
                <div>
                  <span className={`text-xs font-bold uppercase tracking-widest ${meta.text}`}>{meta.badge}</span>
                  <h3 className="text-xl font-bold mt-1 mb-2">{uc.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    This use case is available on the <strong>{meta.label}</strong> plan.{" "}
                    {uc.plan === "free" && "No credit card required — free forever."}
                    {uc.plan === "pro" && "Start for just $5/year — less than a coffee."}
                    {uc.plan === "pro_plus" && "Start Pro Plus for $15/year."}
                  </p>
                  <Link
                    href={ctaHref}
                    className={`inline-flex items-center gap-2 font-bold px-6 py-3 rounded-xl text-sm shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all text-white bg-gradient-to-br ${meta.hero}`}
                  >
                    {uc.cta} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* ── Section 6: Full-width CTA ── */}
          <section className="text-center">
            <div className={`bg-gradient-to-br ${meta.hero} rounded-3xl p-12 text-white`}>
              <h3 className="text-3xl font-display font-bold mb-3">
                Ready to set this up?
              </h3>
              <p className="text-white/70 mb-8 max-w-md mx-auto">
                {uc.plan === "free" && "It takes 60 seconds. No credit card. No design skills. Just your info and a QR that works."}
                {uc.plan === "pro" && "Pro plan — $5/year. That's less than a single coffee. Unlock analytics, custom branding, and more."}
                {uc.plan === "pro_plus" && "Pro Plus — $15/year. Full feature set for growing businesses and professionals."}
              </p>
              <Link
                href={ctaHref}
                className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold px-8 py-4 rounded-xl shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all"
              >
                {uc.cta} <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </section>

          {/* ── Section 7: Related use cases ── */}
          {related.length > 0 && (
            <section>
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
                <span className="w-8 h-px bg-border" /> Related use cases
              </div>
              <h2 className="text-2xl font-display font-bold mb-8">You might also like</h2>
              <div className="grid sm:grid-cols-3 gap-5">
                {related.map((r) => (
                  <RelatedCard key={r.slug} uc={r} />
                ))}
              </div>
              <div className="mt-8 text-center">
                <Link
                  href="/use-cases"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
                >
                  Browse all 186 use cases <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </section>
          )}

        </div>
      </div>
    </>
  );
}
